/**
 * @file src/Routes.jsx
 * @description Application router — single source of truth for all routes.
 *
 * What changed from the original:
 *  - PersistQueryClientProvider → QueryClientProvider (persistence removed)
 *  - queryClient imported from lib/queryClient (not defined inline here)
 *  - store imported and Provider added (Redux for SSE only)
 *  - InvoiceAutoSaveWrapper removed — moved to invoice-processing-queue page
 *  - All route path strings replaced with ROUTES constants
 *  - OAuth route fixed: component={} → element={} (React Router v6 syntax)
 *  - ReactQueryDevtools shown in dev only
 *  - No hardcoded URLs anywhere
 */

import React from "react";
import { Provider } from "react-redux";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";

import { queryClient } from "lib/queryClient";
import { store } from "store";
import { ROUTES } from "config/routes";

import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import ProtectedRoute from "components/ui/ProtectedRoute";

// Pages
import NotFound from "pages/NotFound";
import AuthenticationModal from "pages/authentication-modal/AuthenticationModal";
import OAuthCallback from "pages/oAuthCallBack/AuthCallback";
import CompanyOnboardingWizard from "pages/company-onboarding-wizard";
import CompanySetupSuccess from "pages/company-setup-success";
import CompanySelect from "pages/company-select";
import DashboardOverview from "pages/dashboard-overview";
import InvoiceManagementHub from "pages/invoice-management-hub";
import InvoiceCreationWizard from "pages/invoice-creation-wizard";
import InvoiceProcessingQueue from "pages/invoice-processing-queue";
import ProcessingCompletionSummary from "pages/processing-completion-summary";
import InvoiceErrorCorrection from "pages/invoice-error-correction";
import UploadInvoice from "pages/upload-invoice";
import AuditLogs from "pages/audit-logs";
import CompanySettings from "pages/company-settings";
import UserManagement from "pages/user-management";
import DeviceSettings from "pages/device-settings";
import ClientManagement from "pages/client-management";
import ProductManagement from "pages/product-management";
import PaymentManagement from "pages/payment-management";
import Refunds from "pages/refunds";
import VendorManagement from "pages/vendor-management";
import PurchaseOrders from "pages/purchase-orders";
import PurchaseOrderWizard from "pages/purchase-orders/PurchaseOrderWizard";
import Expenses from "pages/purchases/expenses";
import OperatingExpense from "pages/purchases/expenses/OperatingExpense";
import PurchaseBill from "pages/purchases/expenses/PurchaseBill";
import OfficeEquipment from "pages/purchases/expenses/OfficeEquipment";
import PurchasePayments from "pages/purchases/payments";
import PurchaseRefunds from "pages/purchases/refunds";
import PurchaseRefundWizard from "pages/purchases/refunds/PurchaseRefundWizard";
import PurchaseCreditNoteWizard from "pages/purchases/refunds/PurchaseCreditNoteWizard";
import PurchasesDashboard from "pages/purchases/dashboard";
import BranchManagement from "pages/branch-management";
import AccountingPage from "pages/accounting";
import AcceptInvite from "pages/accept-invite";

const Protected = ({ children, requireCompany = true }) => (
  <ProtectedRoute requireCompany={requireCompany}>{children}</ProtectedRoute>
);

const BetaLoginPage = () => (
  <AuthenticationModal isOpen={true} onClose={() => {}} />
);

const Routes = () => {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}

        <BrowserRouter>
          <ErrorBoundary>
            <ScrollToTop />

            <RouterRoutes>
              {/* Public */}
              <Route path={ROUTES.LANDING} element={<BetaLoginPage />} />

              <Route path={ROUTES.LOGIN} element={<BetaLoginPage />} />

              <Route path={ROUTES.OAUTH_CALLBACK} element={<OAuthCallback />} />

              <Route path={ROUTES.ACCEPT_INVITE} element={<AcceptInvite />} />

              {/* Onboarding */}
              <Route
                path={ROUTES.COMPANY_ONBOARDING}
                element={
                  <Protected requireCompany={false}>
                    <CompanyOnboardingWizard />
                  </Protected>
                }
              />

              <Route
                path={ROUTES.COMPANY_SUCCESS}
                element={
                  <Protected requireCompany={false}>
                    <CompanySetupSuccess />
                  </Protected>
                }
              />

              <Route
                path={ROUTES.COMPANY_SELECT}
                element={
                  <Protected requireCompany={false}>
                    <CompanySelect />
                  </Protected>
                }
              />

              {/* Dashboard */}
              <Route
                path={ROUTES.DASHBOARD}
                element={
                  <Protected>
                    <DashboardOverview />
                  </Protected>
                }
              />

              {/* Accounting */}
              <Route
                path={ROUTES.ACCOUNTING}
                element={
                  <Protected>
                    <AccountingPage />
                  </Protected>
                }
              />

              {/* Invoices */}
              <Route
                path={ROUTES.INVOICE_HUB}
                element={
                  <Protected>
                    <InvoiceManagementHub />
                  </Protected>
                }
              />

              <Route
                path={ROUTES.INVOICE_CREATE}
                element={
                  <Protected>
                    <InvoiceCreationWizard />
                  </Protected>
                }
              />

              <Route
                path={ROUTES.INVOICE_QUEUE}
                element={
                  <Protected>
                    <InvoiceProcessingQueue />
                  </Protected>
                }
              />

              <Route
                path={ROUTES.INVOICE_UPLOAD}
                element={
                  <Protected>
                    <UploadInvoice />
                  </Protected>
                }
              />

              <Route
                path={ROUTES.INVOICE_COMPLETION}
                element={
                  <Protected>
                    <ProcessingCompletionSummary />
                  </Protected>
                }
              />

              <Route
                path={ROUTES.INVOICE_CORRECTION}
                element={
                  <Protected>
                    <InvoiceErrorCorrection />
                  </Protected>
                }
              />

              {/* Clients and vendors */}
              <Route
                path={ROUTES.CLIENT_MANAGEMENT}
                element={
                  <Protected>
                    <ClientManagement />
                  </Protected>
                }
              />

              <Route
                path={ROUTES.VENDOR_MANAGEMENT}
                element={
                  <Protected>
                    <VendorManagement />
                  </Protected>
                }
              />

              {/* Products and payments */}
              <Route
                path={ROUTES.PRODUCT_MANAGEMENT}
                element={
                  <Protected>
                    <ProductManagement />
                  </Protected>
                }
              />

              <Route
                path={ROUTES.PAYMENT_MANAGEMENT}
                element={
                  <Protected>
                    <PaymentManagement />
                  </Protected>
                }
              />

              {/* Branches */}
              <Route
                path={ROUTES.BRANCH_MANAGEMENT}
                element={
                  <Protected>
                    <BranchManagement />
                  </Protected>
                }
              />

              {/* Refunds */}
              <Route
                path={ROUTES.REFUNDS}
                element={
                  <Protected>
                    <Refunds />
                  </Protected>
                }
              />

              {/* Purchase orders */}
              <Route
                path={ROUTES.PURCHASE_ORDERS}
                element={
                  <Protected>
                    <PurchaseOrders />
                  </Protected>
                }
              />

              <Route
                path={ROUTES.PURCHASE_ORDER_NEW}
                element={
                  <Protected>
                    <PurchaseOrderWizard />
                  </Protected>
                }
              />

              {/* Purchases */}
              <Route
                path={ROUTES.PURCHASES_DASHBOARD}
                element={
                  <Protected>
                    <PurchasesDashboard />
                  </Protected>
                }
              />

              <Route
                path={ROUTES.PURCHASES_EXPENSES}
                element={
                  <Protected>
                    <Expenses />
                  </Protected>
                }
              />

              <Route
                path={ROUTES.PURCHASES_OPERATING}
                element={
                  <Protected>
                    <OperatingExpense />
                  </Protected>
                }
              />

              <Route
                path={ROUTES.PURCHASES_BILL}
                element={
                  <Protected>
                    <PurchaseBill />
                  </Protected>
                }
              />

              <Route
                path={ROUTES.PURCHASES_EQUIPMENT}
                element={
                  <Protected>
                    <OfficeEquipment />
                  </Protected>
                }
              />

              <Route
                path={ROUTES.PURCHASES_PAYMENTS}
                element={
                  <Protected>
                    <PurchasePayments />
                  </Protected>
                }
              />

              <Route
                path={ROUTES.PURCHASES_REFUNDS}
                element={
                  <Protected>
                    <PurchaseRefunds />
                  </Protected>
                }
              />

              <Route
                path={ROUTES.PURCHASES_REFUND_NEW}
                element={
                  <Protected>
                    <PurchaseRefundWizard />
                  </Protected>
                }
              />

              <Route
                path={ROUTES.PURCHASES_CN_NEW}
                element={
                  <Protected>
                    <PurchaseCreditNoteWizard />
                  </Protected>
                }
              />

              {/* Settings and administration */}
              <Route
                path={ROUTES.COMPANY_SETTINGS}
                element={
                  <Protected>
                    <CompanySettings />
                  </Protected>
                }
              />

              <Route
                path={ROUTES.USER_MANAGEMENT}
                element={
                  <Protected>
                    <UserManagement />
                  </Protected>
                }
              />

              <Route
                path={ROUTES.DEVICE_SETTINGS}
                element={
                  <Protected>
                    <DeviceSettings />
                  </Protected>
                }
              />

              <Route
                path={ROUTES.AUDIT_LOGS}
                element={
                  <Protected>
                    <AuditLogs />
                  </Protected>
                }
              />

              {/* Fallback */}
              <Route path={ROUTES.NOT_FOUND} element={<NotFound />} />
            </RouterRoutes>
          </ErrorBoundary>
        </BrowserRouter>
      </QueryClientProvider>
    </Provider>
  );
};

export default Routes;
